import { Loader } from "@tfg/ui"
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { OrganizationType } from "@tfg/types/types/organization.types"
import { UserRoles } from "@tfg/types/types/user.types"

import { httpClient } from "~/backend/axios"
import { clearAllAdminOnboardingBypasses } from "~/pages/SupplierPortal/Onboarding/utils/adminOnboardingBypass"

type AuthOrganizationProfilePatch = Readonly<{
  id: string
  name: string
  street?: string | null
  city?: string | null
  postalCode?: string | null
  country?: string | null
}>

type AuthContext = {
  isAuthenticated: boolean
  isLoading: boolean
  user: LibraryResponses.TFGUser | null
  isAdmin: boolean
  logout: () => Promise<void>
  authenticate: (user: LibraryResponses.TFGUser) => void
  checkAuth: () => Promise<void>
  patchAuthUserOrganization: (
    organization: AuthOrganizationProfilePatch
  ) => void
  homeRedirectUrl: string
  getHomeRedirectUrl: (user: LibraryResponses.TFGUser) => string
}

const DEFAULT_HOME_REDIRECT_URL = "/"
export const SUPPLIER_HOME_REDIRECT_URL = "/supplier/requests/traceability"

export const getHomeRedirectUrlForUser = (
  user: LibraryResponses.TFGUser
): string => {
  if (!user?.organization) return DEFAULT_HOME_REDIRECT_URL
  return [OrganizationType.BRAND, OrganizationType.PLATFORM_ADMIN].includes(
    user.organization.type
  )
    ? DEFAULT_HOME_REDIRECT_URL
    : SUPPLIER_HOME_REDIRECT_URL
}

const noopAsync = (): Promise<void> => Promise.resolve()
const noop = (): void => {}

export const AuthContext = createContext<AuthContext>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  isAdmin: false,
  logout: noopAsync,
  authenticate: noop,
  checkAuth: noopAsync,
  patchAuthUserOrganization: noop,
  homeRedirectUrl: DEFAULT_HOME_REDIRECT_URL,
  getHomeRedirectUrl: () => DEFAULT_HOME_REDIRECT_URL,
})

type Props = PropsWithChildren<object>

export function AuthContextProvider({ children }: Props) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [user, setUser] = useState<LibraryResponses.TFGUser | null>(null)
  const [homeRedirectUrl, setHomeRedirectUrl] = useState<string>(
    DEFAULT_HOME_REDIRECT_URL
  )
  const hasResolvedInitialAuthRef = useRef(false)

  const logout = useCallback(async () => {
    try {
      await httpClient.post("/auth/logout")
    } finally {
      clearAllAdminOnboardingBypasses()
      setIsAuthenticated(false)
      setUser(null)
      setHomeRedirectUrl(DEFAULT_HOME_REDIRECT_URL)
      setIsLoading(false)
      hasResolvedInitialAuthRef.current = true
    }
  }, [])

  const isAdmin = user?.role === UserRoles.SUPER_ADMIN
  const getHomeRedirectUrl = useCallback(getHomeRedirectUrlForUser, [])

  const authenticate = useCallback(
    (user: LibraryResponses.TFGUser) => {
      setUser(user)
      setHomeRedirectUrl(getHomeRedirectUrl(user))
      setIsAuthenticated(true)
      setIsLoading(false)
      hasResolvedInitialAuthRef.current = true
    },
    [getHomeRedirectUrl]
  )

  const patchAuthUserOrganization = useCallback(
    (organization: AuthOrganizationProfilePatch): void => {
      setUser((previousUser) => {
        if (!previousUser) {
          return previousUser
        }

        return {
          ...previousUser,
          organization: {
            ...previousUser.organization,
            id: organization.id,
            name: organization.name,
            street: organization.street ?? previousUser.organization.street,
            city: organization.city ?? previousUser.organization.city,
            postalCode:
              organization.postalCode ?? previousUser.organization.postalCode,
            country:
              (organization.country as typeof previousUser.organization.country) ??
              previousUser.organization.country,
          },
        }
      })
    },
    []
  )

  const checkAuth = useCallback(async () => {
    setIsLoading(true)
    try {
      await httpClient.post("/auth/refresh-token").then((response) => {
        const user: LibraryResponses.TFGUser = response.data
        if (user && user?.organization) {
          setUser(user)
          setHomeRedirectUrl(getHomeRedirectUrl(user))
          setIsAuthenticated(true)
        } else {
          setIsAuthenticated(false)
        }
        setIsLoading(false)
      })
    } catch {
      setIsAuthenticated(false)
      setIsLoading(false)
    }
  }, [getHomeRedirectUrl])

  useEffect(() => {
    ;(async () => {
      try {
        await checkAuth()
      } catch {
        logout()
      }
    })()
  }, [checkAuth])

  const value = useMemo(
    (): AuthContext => ({
      isAuthenticated,
      isLoading,
      user,
      isAdmin,
      logout,
      authenticate,
      checkAuth,
      patchAuthUserOrganization,
      homeRedirectUrl,
      getHomeRedirectUrl,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      isAdmin,
      logout,
      authenticate,
      checkAuth,
      patchAuthUserOrganization,
      homeRedirectUrl,
      getHomeRedirectUrl,
    ]
  )

  return (
    <AuthContext.Provider value={value}>
      {isLoading ? <Loader fullPage /> : children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within a AuthContextProvider")
  }
  return context
}
